/**
 * PortraitFramingResponder — answers "where is the model's head and
 * shoulders on the canvas RIGHT NOW?" for the save-time portrait
 * capture in the shared avatar-settings modal (Aug 11 2026).
 *
 * Why this exists: the capture path used to crop with fixed constants
 * tuned against one canonical zoom, which assumed every model's head
 * projects to the same canvas position. Models differ (UE5 skeletons
 * with `positionY` offsets vs GymGirl FBX rigs vs Arc), so Blake sat
 * lower in the frame while others were fine, and each global re-tune
 * fixed one model by breaking another. This component makes framing
 * model-agnostic: it measures the LOADED geometry, so any future model
 * is framed correctly with no per-model tuning.
 *
 * Scalp anchoring (Aug 11 2026, Keith's 5th review): the reported
 * head-top is the top of the SCALP — hair meshes (matched by name on
 * the object, its ancestors, or its material) are excluded from the
 * head-top/centre measurement so ponytails and buns don't push the
 * face down or sideways in the crop.
 *
 * Wire contract (window events, same pattern as `avatar:config:update`):
 *
 *   request  `avatar:portrait:requestFraming` detail: { requestId }
 *   response `avatar:portrait:framing`        detail: {
 *     requestId, avatarInstanceId?, canvas (the WebGL canvas element,
 *     used by hosts to disambiguate multiple mounted scenes),
 *     canvasWidth/canvasHeight (drawing-buffer px),
 *     centerX, headTopY, shoulderY (drawing-buffer px),
 *     headTopWorldY, shoulderWorldY (diagnostics, world units)
 *   }
 *
 * The response is dispatched synchronously from the request handler, so
 * a requester that listens before dispatching gets it in the same tick.
 * If the model has no geometry yet (still loading) we stay silent and
 * the requester's timeout falls back to its legacy fixed-constant crop.
 */

import React, { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

export const PORTRAIT_FRAMING_REQUEST_EVENT = 'avatar:portrait:requestFraming';
export const PORTRAIT_FRAMING_RESPONSE_EVENT = 'avatar:portrait:framing';

/**
 * Bones whose pivot sits on the shoulder line. Covers the rigs in the
 * registry: UE5 MetaHuman (`clavicle_l/r`, `upperarm_l/r`), Mixamo-style
 * FBX (`LeftShoulder`, `mixamorig:LeftArm`), and generic exports.
 * `upper_?arm` deliberately does NOT match `forearm`/`lowerarm`.
 */
const SHOULDER_BONE_PATTERN = /(clavicle|shoulder|upper_?arm)/i;
/** Exact matches (after prefix strip + lowercase) for Mixamo arm roots. */
const SHOULDER_BONE_EXACT = new Set(['leftarm', 'rightarm', 'arm_l', 'arm_r', 'l_arm', 'r_arm']);

/** Max vertices sampled per mesh when measuring bounds (one-shot cost). */
const MAX_SAMPLED_VERTICES = 20000;

/**
 * Hair naming across the registry's rigs (verified against every GLB in
 * the avatar-library, Aug 11 2026):
 *   - UE5 M4/F4: mesh nodes `SK_M4_Hair_01`, `SK_F4_Hair_Bun_Yuki`, …
 *   - Cara (f4-xandra): `Yuki_Hair`, `Yuki_Bangs`
 *   - GymGirls: anonymous mesh names (`Mesh.013`) but MEANINGFUL node
 *     names (`SK_GYMGIRLS_HAIR_07`, `SK_GYMGIRLS_HAIR_BANGS_06`) — which
 *     GLTFLoader assigns to the runtime objects, so an ancestor-name walk
 *     sees them.
 *   - default (Arc female): `hair_6_cards.004`
 * `pony`/`bun` cover future exports; material names are checked too.
 */
const HAIR_NAME_PATTERN = /(hair|bangs|yuki|pony|bun)/i;

function isHairObject(object: THREE.Object3D): boolean {
  let node: THREE.Object3D | null = object;
  while (node) {
    if (node.name && HAIR_NAME_PATTERN.test(node.name)) return true;
    node = node.parent;
  }
  const material = (object as THREE.Mesh).material;
  const materials = Array.isArray(material) ? material : material ? [material] : [];
  return materials.some((mat) => mat?.name && HAIR_NAME_PATTERN.test(mat.name));
}

function normalizedBoneName(name: string): string {
  const parts = name.split(':');
  return (parts[parts.length - 1] || name).toLowerCase();
}

function isWorldVisible(object: THREE.Object3D): boolean {
  let node: THREE.Object3D | null = object;
  while (node) {
    if (!node.visible) return false;
    node = node.parent;
  }
  return true;
}

export interface PortraitFramingResponderProps {
  avatarInstanceId?: string;
}

export default function PortraitFramingResponder({ avatarInstanceId }: PortraitFramingResponderProps) {
  const { scene, camera, gl } = useThree();

  useEffect(() => {
    const handleRequest = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      const requestId = detail.requestId;
      if (!requestId) return;

      try {
        scene.updateMatrixWorld(true);
        camera.updateMatrixWorld();

        // --- Pass 1: world-space bounds of the visible, pose-applied
        // geometry. `getVertexPosition` applies morphs AND (for
        // SkinnedMesh) current bone transforms, so the measurement is
        // true for the frame being captured — including per-model
        // group `scale` / `positionY` offsets via matrixWorld.
        //
        // Hair meshes are tracked separately (Aug 11 2026, Keith's 5th
        // review — "anchor on the scalp"): a ponytail or bun used to
        // inflate the head-top measurement, so models like Sienna had
        // their face pushed DOWN in the portrait to make room for hair.
        // The head-top we report is the SCALP (max Y of non-hair
        // geometry); hair bounds only feed the sanity checks.
        const v = new THREE.Vector3();
        let minX = Infinity;
        let maxX = -Infinity;
        let minY = Infinity;
        let maxY = -Infinity;
        let scalpMinX = Infinity;
        let scalpMaxX = -Infinity;
        let scalpMaxY = -Infinity;
        let sampled = 0;
        let scalpSampled = 0;

        scene.traverse((object) => {
          const mesh = object as THREE.Mesh;
          if (!(mesh as unknown as { isMesh?: boolean }).isMesh) return;
          if (!isWorldVisible(mesh)) return;
          const position = mesh.geometry?.getAttribute('position');
          if (!position || position.count === 0) return;
          const hair = isHairObject(mesh);
          const stride = Math.max(1, Math.ceil(position.count / MAX_SAMPLED_VERTICES));
          for (let i = 0; i < position.count; i += stride) {
            mesh.getVertexPosition(i, v);
            v.applyMatrix4(mesh.matrixWorld);
            if (!Number.isFinite(v.x) || !Number.isFinite(v.y)) continue;
            if (v.x < minX) minX = v.x;
            if (v.x > maxX) maxX = v.x;
            if (v.y < minY) minY = v.y;
            if (v.y > maxY) maxY = v.y;
            sampled += 1;
            if (!hair) {
              if (v.x < scalpMinX) scalpMinX = v.x;
              if (v.x > scalpMaxX) scalpMaxX = v.x;
              if (v.y > scalpMaxY) scalpMaxY = v.y;
              scalpSampled += 1;
            }
          }
        });

        // Model not loaded yet → stay silent, requester times out and
        // uses its fallback crop.
        if (sampled === 0 || !Number.isFinite(maxY) || maxY <= minY) return;

        // Scalp anchor: prefer the non-hair head top. Guard against a
        // degenerate exclusion (all meshes name-matched as hair, or the
        // non-hair top landing implausibly low — below 80% of the full
        // silhouette height, which would mean the body mesh has no head
        // and the "scalp" is really a collar) by falling back to the
        // full-geometry top.
        const fullHeight = maxY - minY;
        const scalpIsSane =
          scalpSampled > 0 &&
          Number.isFinite(scalpMaxY) &&
          scalpMaxY > minY + fullHeight * 0.8;
        const headTopWorldY = scalpIsSane ? scalpMaxY : maxY;

        // --- Pass 2: shoulder line from the skeleton (joint pivots).
        let shoulderWorldY: number | null = null;
        scene.traverse((object) => {
          if (!(object as unknown as { isBone?: boolean }).isBone) return;
          const name = normalizedBoneName(object.name || '');
          if (!SHOULDER_BONE_PATTERN.test(name) && !SHOULDER_BONE_EXACT.has(name)) return;
          object.getWorldPosition(v);
          if (!Number.isFinite(v.y)) return;
          if (shoulderWorldY === null || v.y > shoulderWorldY) shoulderWorldY = v.y;
        });

        // Sanity: the shoulder line must sit inside the body, below the
        // scalp. Otherwise fall back to anthropometry (shoulders at
        // ~81.5% of scalp-to-feet stature for a standing figure).
        const stature = headTopWorldY - minY;
        if (
          shoulderWorldY === null ||
          shoulderWorldY >= headTopWorldY - stature * 0.05 ||
          shoulderWorldY <= minY
        ) {
          shoulderWorldY = headTopWorldY - stature * 0.185;
        }

        // --- Project to drawing-buffer pixels. Camera is orthographic
        // facing -z, so x/y are z-independent; project at z=0.
        // Horizontal centre comes from the non-hair silhouette too, so
        // an off-axis ponytail can't pull the face sideways.
        const canvas = gl.domElement;
        const centerWorldX = scalpIsSane && scalpMaxX > scalpMinX
          ? (scalpMinX + scalpMaxX) / 2
          : (minX + maxX) / 2;
        const project = (worldX: number, worldY: number) => {
          const p = new THREE.Vector3(worldX, worldY, 0).project(camera);
          return {
            x: ((p.x + 1) / 2) * canvas.width,
            y: ((1 - p.y) / 2) * canvas.height,
          };
        };
        const headTop = project(centerWorldX, headTopWorldY);
        const shoulder = project(centerWorldX, shoulderWorldY);

        window.dispatchEvent(new CustomEvent(PORTRAIT_FRAMING_RESPONSE_EVENT, {
          detail: {
            requestId,
            avatarInstanceId,
            canvas,
            canvasWidth: canvas.width,
            canvasHeight: canvas.height,
            centerX: headTop.x,
            headTopY: headTop.y,
            shoulderY: shoulder.y,
            headTopWorldY,
            shoulderWorldY,
          },
        }));
      } catch (error) {
        // Measurement is best-effort: a failure here must never break
        // the scene. The requester's timeout handles the silence.
        console.warn('[PortraitFraming] measurement failed:', error);
      }
    };

    window.addEventListener(PORTRAIT_FRAMING_REQUEST_EVENT, handleRequest);
    return () => window.removeEventListener(PORTRAIT_FRAMING_REQUEST_EVENT, handleRequest);
  }, [scene, camera, gl, avatarInstanceId]);

  return null;
}
